'use client';

import type { SearchResult } from '@/lib/search-client';
import { uiFb } from '@/lib/i18n';

interface RecentItem {
  query: string;
  href: string;
  title: string;
}

interface CommandPaletteEmptyProps {
  query: string;
  hasResults: boolean;
  isLoading: boolean;
  shardError: boolean;
  recentSearches: RecentItem[];
  onClearRecent: () => void;
  onNavigate: (href: string) => void;
  onRetry?: () => void;
  lang: string;
}

const SUGGESTIONS_BY_LANG: Record<string, Array<{ label: string; href: string }>> = {
  en: [
    { label: 'Indonesia', href: '/indonesia' },
    { label: 'Thailand', href: '/thailand' },
    { label: 'Long Weekends', href: '/indonesia-long-weekends-2026' },
    { label: 'Best Time to Visit', href: '/best-time-to-visit-bali' },
  ],
  id: [
    { label: 'Indonesia', href: '/indonesia' },
    { label: 'SNBP 2026', href: '/snbp-2026' },
    { label: 'Libur Panjang', href: '/indonesia-long-weekends-2026' },
    { label: 'Hari Raya', href: '/events/eid-al-fitr-2026-indonesia' },
  ],
  ar: [
    { label: '\u0627\u0644\u0633\u0639\u0648\u062F\u064A\u0629', href: '/saudi-arabia' },
    { label: '\u0645\u0635\u0631', href: '/egypt' },
    { label: '\u0627\u0644\u0625\u0645\u0627\u0631\u0627\u062A', href: '/united-arab-emirates' },
    { label: '\u0627\u0644\u0639\u0631\u0627\u0642', href: '/iraq' },
  ],
};

const DEFAULT_SUGGESTIONS = SUGGESTIONS_BY_LANG.en;

function getSuggestions(lang: string) {
  return SUGGESTIONS_BY_LANG[lang] || DEFAULT_SUGGESTIONS;
}

export function CommandPaletteEmpty({
  query,
  hasResults,
  isLoading,
  shardError,
  recentSearches,
  onClearRecent,
  onNavigate,
  onRetry,
  lang,
}: CommandPaletteEmptyProps) {
  // Loading state
  if (isLoading) {
    return <CommandPaletteSkeleton lang={lang} />;
  }

  // Error state with retry button
  if (shardError) {
    return (
      <div className="px-4 py-8 text-center">
        <p className="text-sm text-text-dim">
          {uiFb('ui.search.error_unavailable', 'Search is temporarily unavailable.', lang)}
        </p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="mt-3 rounded-lg border border-border-subtle px-4 py-2 min-h-[44px] text-sm text-text-secondary transition-colors hover:bg-surface-hover hover:text-text-primary"
          >
            {uiFb('ui.search.try_again', 'Try again', lang)}
          </button>
        )}
        <div className="mt-4">
          <span className="text-caption font-semibold uppercase tracking-wider text-text-dim">
            {uiFb('ui.search.suggestions', 'Suggestions', lang)}
          </span>
          <div className="mt-1.5 flex flex-wrap justify-center gap-1.5">
            {getSuggestions(lang).map((s) => (
              <button
                key={s.href}
                onClick={() => onNavigate(s.href)}
                className="rounded-full border border-border-subtle px-3 py-1 min-h-[44px] text-label text-text-secondary transition-colors hover:bg-surface-hover hover:text-text-primary"
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // No results for a query — actionable with suggestions + hub links
  if (query.length >= 2 && !hasResults) {
    const hubLinks = [
      { label: uiFb('ui.search.hub_countries', 'Countries', lang), href: '/' },
      { label: uiFb('ui.search.hub_events', 'Events', lang), href: '/events-by-type' },
      { label: uiFb('ui.search.hub_months', 'Months', lang), href: '/events-by-month' },
    ];
    return (
      <div className="px-4 py-6 text-center">
        <p className="text-sm text-text-dim">
          {uiFb('ui.search.no_results', 'No results for', lang)} &ldquo;{query}&rdquo;
        </p>
        <p className="mt-1 text-label text-text-dim">
          {uiFb('ui.search.try_different', 'Try a different search term or browse by category', lang)}
        </p>
        <div className="mt-4">
          <span className="text-caption font-semibold uppercase tracking-wider text-text-dim">
            {uiFb('ui.search.suggestions', 'Suggestions', lang)}
          </span>
          <div className="mt-1.5 flex flex-wrap justify-center gap-1.5">
            {getSuggestions(lang).map((s) => (
              <button
                key={s.href}
                onClick={() => onNavigate(s.href)}
                className="rounded-full border border-border-subtle px-3 py-1 min-h-[44px] text-label text-text-secondary transition-colors hover:bg-surface-hover hover:text-text-primary"
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-3">
          <span className="text-caption font-semibold uppercase tracking-wider text-text-dim">
            {uiFb('ui.search.browse', 'Browse', lang)}
          </span>
          <div className="mt-1.5 flex flex-wrap justify-center gap-1.5">
            {hubLinks.map((h) => (
              <button
                key={h.href}
                onClick={() => onNavigate(h.href)}
                className="rounded-full border border-accent-primary/30 bg-accent-primary/5 px-3 py-1 min-h-[44px] text-label text-accent-primary transition-colors hover:bg-accent-primary/10"
              >
                {h.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Empty state (no query)
  return (
    <div className="px-4 py-4">
      {recentSearches.length > 0 && (
        <div className="mb-3">
          <div className="flex items-center justify-between">
            <span className="text-caption font-semibold uppercase tracking-wider text-text-dim">
              {uiFb('ui.search.recent', 'Recent', lang)}
            </span>
            <button
              onClick={onClearRecent}
              className="text-caption text-text-dim hover:text-text-secondary min-h-[44px] px-2"
            >
              {uiFb('ui.search.clear', 'Clear', lang)}
            </button>
          </div>
          <div className="mt-1.5 space-y-0.5">
            {recentSearches.map((item) => (
              <button
                key={item.href}
                onClick={() => onNavigate(item.href)}
                className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 min-h-[44px] text-start text-detail text-text-secondary transition-colors hover:bg-surface-hover"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="shrink-0 text-text-dim" aria-hidden="true">
                  <polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                </svg>
                <span className="truncate">{item.title}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <div>
        <span className="text-caption font-semibold uppercase tracking-wider text-text-dim">
          {uiFb('ui.search.suggestions', 'Suggestions', lang)}
        </span>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {getSuggestions(lang).map((s) => (
            <button
              key={s.href}
              onClick={() => onNavigate(s.href)}
              className="rounded-full border border-border-subtle px-3 py-1 min-h-[44px] text-label text-text-secondary transition-colors hover:bg-surface-hover hover:text-text-primary"
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function CommandPaletteSkeleton({ lang }: { lang: string }) {
  return (
    <div className="space-y-2 p-4" role="status" aria-label={uiFb('ui.search.loading_label', 'Loading search results', lang)}>
      <p className="text-sm text-text-dim text-center py-1">
        {uiFb('ui.search.loading', 'Searching...', lang)}
      </p>
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 rounded-lg px-3 py-2">
          <div className="h-5 w-5 animate-pulse rounded bg-surface-hover" />
          <div className="flex-1 space-y-1">
            <div className="h-3.5 w-3/4 animate-pulse rounded bg-surface-hover" />
            <div className="h-2.5 w-1/2 animate-pulse rounded bg-surface-hover" />
          </div>
        </div>
      ))}
    </div>
  );
}
